/**
 * O cartão em volta de cada gráfico: título, subtítulo, troca de visão
 * (Barras/Corrida), botão "Tabela" (os mesmos números em tabela, para ler
 * sem depender de cor ou do mouse), botão "Baixar imagem" e notas de rodapé.
 *
 * Lê: só as props.
 * Usado por: telas/Painel.tsx.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { baixarComoPng } from '../graficos/exportar.ts';
import { usarLargura } from '../graficos/usar-largura.ts';

export interface TabelaDoGrafico {
  colunas: string[];
  linhas: string[][];
  /** Índices das colunas de números (alinhadas à direita). */
  numericas?: number[];
}

interface Props {
  titulo: string;
  subtitulo?: string;
  notas?: string[];
  visoes?: Array<{ id: string; rotulo: string }>;
  visao?: string;
  aoTrocarVisao?: (id: string) => void;
  tabela: TabelaDoGrafico;
  /** Desenha o gráfico na largura disponível. */
  desenhar: (largura: number) => ReactNode;
  /** O mesmo gráfico, pronto para virar imagem (com título e rodapé). */
  paraImagem?: () => ReactElement;
  nomeImagem?: string;
}

export function CartaoGrafico({ titulo, subtitulo, notas = [], visoes, visao, aoTrocarVisao, tabela, desenhar, paraImagem, nomeImagem }: Props) {
  const [referencia, largura] = usarLargura<HTMLDivElement>();
  const [mostrarTabela, setMostrarTabela] = useState(false);
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function baixar() {
    if (!paraImagem) return;
    setBaixando(true);
    setErro(null);
    try {
      await baixarComoPng(paraImagem(), nomeImagem ?? 'grafico.png');
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : String(falha));
    } finally {
      setBaixando(false);
    }
  }

  return (
    <figure className="cartao cartao-grafico">
      <header className="cartao-grafico-topo">
        <div>
          <h2>{titulo}</h2>
          {subtitulo ? <p className="subtitulo">{subtitulo}</p> : null}
        </div>
        <div className="acoes">
          {visoes && visao && aoTrocarVisao ? (
            <div className="segmentado" role="group" aria-label="Tipo de gráfico">
              {visoes.map((opcao) => (
                <button key={opcao.id} type="button" aria-pressed={opcao.id === visao} onClick={() => aoTrocarVisao(opcao.id)}>
                  {opcao.rotulo}
                </button>
              ))}
            </div>
          ) : null}
          <button type="button" className="botao-secundario" aria-pressed={mostrarTabela} onClick={() => setMostrarTabela((atual) => !atual)}>
            {mostrarTabela ? 'Ver gráfico' : 'Tabela'}
          </button>
          {paraImagem ? (
            <button type="button" className="botao-secundario" onClick={baixar} disabled={baixando}>
              {baixando ? 'Gerando…' : 'Baixar imagem'}
            </button>
          ) : null}
        </div>
      </header>
      {erro ? <p className="mensagem-erro">Não deu para baixar: {erro}</p> : null}
      <div ref={referencia} className="cartao-grafico-corpo">
        {mostrarTabela ? (
          <div className="tabela-rolavel">
            <table className="tabela">
              <thead>
                <tr>
                  {tabela.colunas.map((coluna, i) => (
                    <th key={coluna} className={tabela.numericas?.includes(i) ? 'numero' : undefined}>
                      {coluna}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tabela.linhas.map((linha, i) => (
                  <tr key={i}>
                    {linha.map((celula, j) => (
                      <td key={j} className={tabela.numericas?.includes(j) ? 'numero' : undefined}>
                        {celula}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : largura > 0 ? (
          desenhar(largura)
        ) : null}
      </div>
      {notas.length > 0 ? (
        <figcaption className="notas">
          {notas.map((nota) => (
            <p key={nota}>{nota}</p>
          ))}
        </figcaption>
      ) : null}
    </figure>
  );
}
